import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import '../../accounting/accounting.css';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid';
import { AppToolbar } from '../../../components/AppToolbar';
import { FormTitle } from '../../../components/FormTitle';
import { ExportMenu } from '../../accounting/ExportMenu';
import { useNotify } from '../../../components/notifications';
import { useFetchMs } from '../../../hooks/useFetchMs';
import {
  useCompanies, useDepartments, useDivisionDepartments,
  useLinkDivisionDepartment, useUnlinkDivisionDepartment, errMessage,
  type Company, type Department,
} from './systemMasters';

interface Props { onHome?: () => void; }

/**
 * Settings ▸ System Management ▸ Division Department — LIMS Company_ListView_Copy.
 * Lists every Division (Company) with its joined Department names. Clicking the
 * Department cell opens the multi-select Department picker (pre-checked with the
 * division's current links); OK links the newly-checked and unlinks the unchecked —
 * exactly like the legacy XAF collection-editor on this column.
 */
export function DivisionDepartmentPage({ onHome }: Props) {
  const notify = useNotify();
  const qc = useQueryClient();
  const { data: rows = [], isFetching, refetch } = useCompanies();
  const { data: allDepts = [] } = useDepartments();
  const loadMs = useFetchMs(isFetching);
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(new Set());

  // ── Department cell → multi-select picker (links for ONE division) ──
  const [pickDivisionId, setPickDivisionId] = useState<string | null>(null);
  const [pickSearch, setPickSearch] = useState('');
  const [checked, setChecked] = useState<Set<string | number>>(new Set());
  const [busy, setBusy] = useState(false);
  const initedFor = useRef<string | null>(null);
  const linkedQuery = useDivisionDepartments(pickDivisionId ?? undefined);
  const pickLinked = linkedQuery.data;
  const link = useLinkDivisionDepartment(pickDivisionId ?? '', 'division-department');
  const unlink = useUnlinkDivisionDepartment(pickDivisionId ?? '', 'division-department');

  // Pre-check the division's current departments once the picker's links have loaded.
  useEffect(() => {
    if (pickDivisionId && !linkedQuery.isFetching && pickLinked && initedFor.current !== pickDivisionId) {
      setChecked(new Set(pickLinked.map((d) => d.id)));
      initedFor.current = pickDivisionId;
    }
  }, [pickDivisionId, pickLinked, linkedQuery.isFetching]);

  const openPick = (r: Company) => { initedFor.current = null; setChecked(new Set()); setPickSearch(''); setPickDivisionId(r.id); };
  const closePick = () => setPickDivisionId(null);

  // OK: link newly-checked, unlink newly-unchecked, then refresh the grid's Department column.
  const applyPick = async () => {
    if (!pickDivisionId) return;
    setBusy(true);
    try {
      const linkedIds = new Set((pickLinked ?? []).map((d) => d.id));
      for (const d of allDepts) {
        const isChecked = checked.has(d.id);
        const wasLinked = linkedIds.has(d.id);
        if (isChecked && !wasLinked) await link.mutateAsync(d.id);
        else if (!isChecked && wasLinked) await unlink.mutateAsync(d.id);
      }
      await qc.invalidateQueries({ queryKey: ['sm-companies-list'] });   // refresh the Department column
      closePick();
    } catch (e) { notify.error(errMessage(e)); }
    finally { setBusy(false); }
  };

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? rows.filter((r) => [r.name, r.department].some((v) => (v ?? '').toLowerCase().includes(q))) : rows;
  }, [rows, search]);

  const pickRows = useMemo(() => {
    const q = pickSearch.trim().toLowerCase();
    return q ? allDepts.filter((d) => (d.name ?? '').toLowerCase().includes(q)) : allDepts;
  }, [allDepts, pickSearch]);

  const columns: AppColumn<Company>[] = [
    { field: 'name', header: 'Name', width: '50%', minWidth: 220, text: (r) => r.name ?? '',
      compare: (a, b) => (a.name ?? '').localeCompare(b.name ?? '', undefined, { sensitivity: 'base' }) },
    {
      field: 'department', header: 'Department', width: '50%', minWidth: 220, sortable: false, text: (r) => r.department ?? '',
      // Clicking the Department cell opens the picker (LIMS collection-editor parity).
      render: (r) => (
        <span className="dd-dept-cell" title="Click to assign departments" onClick={(e) => { e.stopPropagation(); openPick(r); }}>
          {r.department ? r.department : <span className="dd-dept-empty">— assign —</span>}
        </span>
      ),
    },
  ];
  const pickCols: AppColumn<Department>[] = [
    { field: 'name', header: 'Department', minWidth: 300, text: (r) => r.name ?? '',
      compare: (a, b) => (a.name ?? '').localeCompare(b.name ?? '', undefined, { sensitivity: 'base' }) },
  ];

  return (
    <div className="acc-page">
      <div className="acc-top">
        <div className="acc-bc">
          <a onClick={onHome}>Home</a><i className="ti ti-chevron-right" />
          <span>Settings</span><i className="ti ti-chevron-right" /><span>System Management</span><i className="ti ti-chevron-right" />
          <span style={{ color: '#4a3728', fontWeight: 600 }}>Division Department</span>
        </div>
        {/* BO UI Standard v2: FormTitle + subtitle ON the grey page — never inside the card. */}
        <FormTitle pageId="sm-division-department" loadMs={loadMs ?? null} loading={isFetching} auditFormName="Division Department" />
        <div className="acc-sub">{rows.length} item{rows.length === 1 ? '' : 's'}</div>
      </div>
      <div className="acc-body">
        {/* Action toolbar — its OWN full-width row (BO UI Standard §6); Export far right. */}
        <AppToolbar
          onRefresh={() => void refetch()} refreshing={isFetching}
          right={
            <span className="app-toolbar__spacer">
              <ExportMenu triggerClassName="ca-btn" filename="DivisionDepartment" headers={['Name', 'Department']} rows={visible.map((r) => [r.name ?? '', r.department ?? ''])} />
            </span>
          }
        />
        {/* Search lives in its OWN row BELOW the toolbar (BO UI Standard) — never inside the card. */}
        <div className="list-search-row">
          <div className="list-search"><span className="tb-i">🔍</span>
            <input placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} />
            {search && <span className="list-search-clear" onClick={() => setSearch('')}>✕</span>}
          </div>
        </div>
        {/* ONE white grid card — grid + its single footer only. */}
        <div className="acc-card">
          <AppDataGrid<Company> rows={visible} columns={columns} getRowId={(r) => r.id}
            selectable selectedIds={selectedIds} onSelectionChange={setSelectedIds}
            initialSort={{ key: 'name', dir: 'asc' }} loading={isFetching} emptyText="No divisions found." />
        </div>
      </div>

      {pickDivisionId && (
        <div className="acc-overlay" onMouseDown={closePick}>
          <div className="acc-dialog acc-dialog-pick" onMouseDown={(e) => e.stopPropagation()}>
            <div className="acc-dialog-hdr"><span>Department</span><i className="ti ti-x acc-dialog-x" onClick={closePick} /></div>
            <div className="acc-dialog-body">
              <div className="acc-pick-search"><div className="acc-search"><i className="ti ti-search" />
                <input autoFocus placeholder="Search…" value={pickSearch} onChange={(e) => setPickSearch(e.target.value)} />
                {pickSearch && <i className="ti ti-x acc-search-x" onClick={() => setPickSearch('')} />}
              </div></div>
              <AppDataGrid<Department> rows={pickRows} columns={pickCols} getRowId={(r) => r.id}
                selectable selectedIds={checked} onSelectionChange={setChecked}
                initialSort={{ key: 'name', dir: 'asc' }} loading={linkedQuery.isFetching} emptyText="No departments found."
                defaultPageSize={20} />
            </div>
            <div className="acc-dialog-ftr">
              <button className="ca-btn ca-btn-solid" onClick={() => void applyPick()} disabled={busy}>OK</button>
              <button className="ca-btn" onClick={closePick} disabled={busy}><span className="tb-i tb-i-grey">↩︎</span> Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
